import { useState, useEffect, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { guides, finds, articles } from '@/mocks/guides';

export default function Home() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `https://kaspreadsheet.com/search/?q=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[700px] md:min-h-[800px] flex items-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20vibrant%20editorial%20flatlay%20of%20assorted%20premium%20streetwear%20fashion%20items%20including%20colorful%20sneakers%20hoodies%20sunglasses%20and%20accessories%20arranged%20artfully%20on%20a%20warm%20textured%20backdrop%2C%20rich%20saturated%20colors%2C%20soft%20directional%20lighting%2C%20high%20end%20fashion%20magazine%20photography%20style%2C%20dynamic%20composition&width=1600&height=900&seq=hero-bg-02&orientation=landscape&nocache=true"
            alt="CSSBuy Spreadsheet fashion products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 pt-24 md:pt-28">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6">
              <span className="px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs font-medium text-white border border-white/20">
                {t('independent_resource')}
              </span>
              <span className="px-3 py-1 bg-primary-500/20 backdrop-blur-sm rounded-full text-xs font-medium text-white border border-primary-500/30">
                2026
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 font-heading">
              {t('hero_h1')}
            </h1>

            <p className="text-base md:text-lg text-white/80 leading-relaxed mb-8 max-w-2xl">
              {t('hero_subtitle')}
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 mb-8 max-w-xl">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('hero_search_placeholder')}
                  className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white/95 backdrop-blur-sm text-sm text-foreground-950 placeholder:text-foreground-400 border border-white/20 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3.5 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t('hero_search_button')}
              </button>
            </form>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 mb-8">
              <Link
                to="/cssbuy-spreadsheet/"
                className="px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t('hero_cta_primary')}
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 bg-white/10 backdrop-blur-sm text-white text-sm font-medium rounded-xl hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap"
              >
                {t('hero_cta_secondary')}
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 text-sm text-white/70">
              <span className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-primary-400" />
                {t('hero_trust_1')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line text-primary-400" />
                {t('hero_trust_2')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-search-line text-primary-400" />
                {t('hero_trust_3')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-lock-line text-primary-400" />
                {t('hero_trust_4')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* What Is Section */}
      <section className="section-padding bg-background-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6 font-heading">
            {t('whatis_h2')}
          </h2>
          <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-8">
            {t('whatis_copy')}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-primary-500 text-white text-sm font-bold rounded-xl hover:bg-primary-600 transition-colors shadow-sm"
            >
              {t('btn_main_site')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-8 py-3.5 bg-accent-500 text-white text-sm font-bold rounded-xl hover:bg-accent-600 transition-colors shadow-sm"
            >
              {t('btn_buy_now')}
            </a>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="section-padding bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              {t('categories_h2')}
            </h2>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors"
            >
              {t('categories_cta')}
              <i className="ri-arrow-right-line" />
            </a>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {categories.map((cat) => (
              <a
                key={cat.nameKey}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group relative aspect-[4/3] rounded-xl overflow-hidden bg-background-200 hover:shadow-lg transition-all duration-300"
              >
                <img
                  src={cat.image}
                  alt={cat.label}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <span className="text-sm font-semibold text-white block truncate">
                    {cat.label}
                  </span>
                </div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="w-6 h-6 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <i className="ri-external-link-line text-white text-xs" />
                  </span>
                </div>
              </a>
            ))}
          </div>

          <div className="mt-6 md:hidden text-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-500"
            >
              {t('categories_cta')}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </section>

      {/* Latest Finds Section */}
      <section className="section-padding bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2 font-heading">
              {t('finds_h2')}
            </h2>
            <p className="text-foreground-600">
              {t('finds_sub')}
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-background-200 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100">
                  <th className="text-left px-4 py-3 font-semibold text-foreground-700">{t('finds_table_product')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-700 hidden md:table-cell">{t('finds_table_category')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-700 hidden lg:table-cell">{t('finds_table_source')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-700 hidden lg:table-cell">{t('finds_table_info')}</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-700 hidden sm:table-cell">{t('finds_table_updated')}</th>
                  <th className="text-right px-4 py-3 font-semibold text-foreground-700">{t('finds_table_view')}</th>
                </tr>
              </thead>
              <tbody>
                {finds.map((find, index) => (
                  <tr
                    key={index}
                    className="border-b border-background-100 hover:bg-background-50 transition-colors"
                  >
                    <td className="px-4 py-3.5 font-medium text-foreground-950">{find.product}</td>
                    <td className="px-4 py-3.5 text-foreground-600 hidden md:table-cell">
                      <span className="px-2 py-1 bg-secondary-100 text-secondary-600 rounded-md text-xs font-medium">
                        {find.category}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-foreground-600 hidden lg:table-cell">{find.source}</td>
                    <td className="px-4 py-3.5 text-foreground-500 text-xs hidden lg:table-cell">{find.info}</td>
                    <td className="px-4 py-3.5 text-foreground-500 text-xs hidden sm:table-cell">{find.updated}</td>
                    <td className="px-4 py-3.5 text-right">
                      <a
                        href={find.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-lg hover:bg-primary-600 transition-colors"
                      >
                        <i className="ri-external-link-line" />
                        {t('finds_table_view')}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 text-center">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 text-foreground-700 text-sm font-medium rounded-lg hover:bg-background-200 transition-colors"
            >
              {t('finds_cta')}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </section>

      {/* Guides Section */}
      <section className="section-padding bg-background-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-8 font-heading">
            {t('guides_h2')}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {guides.map((guide) => (
              <Link
                key={guide.key}
                to={guide.url}
                className="group flex flex-col bg-white rounded-xl border border-background-200 overflow-hidden hover:border-primary-300 hover:shadow-md transition-all"
              >
                <div className="aspect-video bg-background-200 relative overflow-hidden">
                  <img
                    src={guide.image}
                    alt={guide.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 w-9 h-9 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center">
                    <i className={`${guide.icon} text-primary-500`} />
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors mb-2">
                    {guide.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {guide.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Preview Section */}
      <section className="section-padding bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              CSSBuy Spreadsheet Guides
            </h2>
            <Link
              to="/guides/"
              className="hidden md:flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors"
            >
              {t('view_all')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={article.url}
                className="group flex flex-col bg-white rounded-xl border border-background-200 overflow-hidden hover:border-primary-300 hover:shadow-md transition-all"
              >
                <div className="aspect-video bg-background-200 relative overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-2.5 py-1 bg-primary-500 text-white text-xs font-medium rounded-md">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors mb-2 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-4 flex-1 line-clamp-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line" />
                      {article.readingTime} {t('article_reading_time')}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line" />
                      {article.updated}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center md:hidden">
            <Link
              to="/guides/"
              className="inline-flex items-center gap-1 text-sm font-medium text-primary-500"
            >
              {t('view_all')}
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Safety Center Section */}
      <section className="section-padding bg-foreground-950 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4 font-heading">
                {t('safety_h2')}
              </h2>
              <p className="text-white/70 leading-relaxed mb-6">
                {t('safety_copy')}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/safety/"
                  className="px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors"
                >
                  {t('safety_cta')}
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-5 py-2.5 bg-white/10 text-white text-sm font-medium rounded-lg hover:bg-white/20 transition-colors border border-white/20"
                >
                  {t('btn_buy_now')}
                </a>
              </div>
            </div>
            <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=A%20minimal%20shield%20and%20padlock%20symbol%20crafted%20from%20soft%20clay%20shapes%20on%20a%20warm%20neutral%20background%20with%20gentle%20shadows%2C%20trust%20and%20security%20concept%2C%20clean%20three%20dimensional%20render%20aesthetic%2C%20warm%20beige%20and%20amber%20tones%2C%20premium%20abstract%20illustration&width=800&height=600&seq=safety-center-02&orientation=landscape&nocache=true"
                alt="Safety Center"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section-padding bg-background-100">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6 font-heading">
            {t('final_cta_h2')}
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/cssbuy-spreadsheet/"
              className="px-6 py-3 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 transition-colors"
            >
              {t('final_cta_primary')}
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 bg-white text-foreground-700 text-sm font-medium rounded-xl hover:bg-background-50 transition-colors border border-background-200"
            >
              {t('final_cta_secondary')}
            </a>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="px-4 md:px-6 lg:px-10 py-8 bg-background-200">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs text-foreground-500 leading-relaxed">
            {t('disclaimer')}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}